import type { AlgoliaSearchService } from '@b2ag/search'
import { convertTag } from '@b2ag/utils/src/convertTag'
import type { HomeAdvertorial } from '../blocs/HomeAdvertorial'
import type { IAdvertorialRepository } from '../repository/IAdvertorialRepository'
import { sortMappedBlocsBySegmentPriority } from '../utils/filterContentListBySegment'

export interface GetAdvertorialBySupplierUseCase {
  (): Promise<HomeAdvertorial | null>
  (keyword: string | string[]): Promise<HomeAdvertorial | null>
}

export interface AdvertorialUseCaseFactory {
  getAdvertorialBySupplierUseCaseFactory(
    advertorialRepository: IAdvertorialRepository,
    searchService: AlgoliaSearchService,
  ): GetAdvertorialBySupplierUseCase
}

export function getAdvertorialBySupplierUseCaseFactory(
  advertorialRepository: IAdvertorialRepository,
  searchService: AlgoliaSearchService,
) {
  function filterAdvertorial(
    suppliers: string[],
    advertorialList: HomeAdvertorial[],
    tags: string[] = [],
  ): HomeAdvertorial | null {
    const formattedSuppliers = suppliers.map((supplier) => supplier.trim().toUpperCase())
    const needToSort = advertorialList.some((adv) =>
      adv.tags.find((tag) => tag.match(/^segment/) && tag !== 'segmentAll'),
    )
    let advertorialBlock = advertorialList.filter((advertorial) =>
      formattedSuppliers.includes(advertorial.supplierName.trim().toUpperCase()),
    )
    if (needToSort) {
      advertorialBlock = advertorialBlock?.sort(sortMappedBlocsBySegmentPriority(tags))
    }
    return advertorialBlock.at(0) || null
  }

  async function getAdvertorialBySupplier(): Promise<HomeAdvertorial | null> {
    const [advertorialList, suppliers] = await Promise.all([
      advertorialRepository.getSupplierAdvertorial(),
      searchService.getSuppliers(),
    ])
    if (!advertorialList) return null
    return filterAdvertorial(suppliers, advertorialList)
  }

  async function getAdvertorialBySupplierAndTags(
    tags: string[],
    keyword: string | string[] = [],
  ): Promise<HomeAdvertorial | null> {
    const keywords: string[] = Array.isArray(keyword) ? keyword.map(convertTag) : [convertTag(keyword)]

    const [advertorialList, suppliers] = await Promise.all([
      advertorialRepository.getSupplierAdvertorialByTags(tags, keywords),
      searchService.getSuppliers(),
    ])
    if (!advertorialList) return null
    return filterAdvertorial(suppliers, advertorialList, tags)
  }

  return {
    getAdvertorialBySupplier,
    getAdvertorialBySupplierAndTags,
  }
}
